export interface CsvColumn<T> {
  /** Column header text (already translated). */
  header: string;
  /** Pulls the raw cell value for a row. */
  accessor: (row: T) => string | number;
}

/** UTF-8 byte-order mark so spreadsheet apps (Excel) detect the encoding. */
const UTF8_BOM = '﻿';

/**
 * Wraps a value in quotes (doubling inner quotes) only when it contains a
 * comma, quote or line break — the RFC 4180 escaping rules.
 */
const escapeCsvValue = (value: string | number): string => {
  const stringValue = String(value);

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Serializes rows into a CSV string: a header row built from the columns,
 * followed by one line per row. Empty data yields just the header.
 */
export const toCsv = <T>(rows: T[], columns: CsvColumn<T>[]): string => {
  const header = columns.map(column => escapeCsvValue(column.header)).join(',');
  const lines = rows.map(row =>
    columns.map(column => escapeCsvValue(column.accessor(row))).join(','),
  );

  return [header, ...lines].join('\n');
};

/** Triggers a client-side download of `csv` as a file. */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([`${UTF8_BOM}${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
